import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import FilterButtonGroup from 'components/filter-button-group';
import ChileOrdersList from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/orders/chile/chile-orders-list';
import StandardOrdersList from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/orders/standard/standard-orders-list';
import ChileRecommendationsList from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/recommendations/chile/chile-recommendations-list';
import StandardRecommendationsList from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/recommendations/standard/standard-recommendations-list';
import { useOrdersState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/orders-context';
import { isConfirmedOrder } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/utils/order-utils';
import { Box } from '@mui/material';
import { useAccountState } from 'contexts/account/account-context';
import { MarketId } from 'types/market-id.enum';
import CreateOrderDialog from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/orders/dialogs/create-order-dialog';
import ViewFeedProgramsDialog from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/orders/dialogs/view-feed-programs-dialog';


type Props = {};

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.orders';

const OrdersSection: React.FC<Props> = ({ }) => {
  const intl = useIntl();
  const { account } = useAccountState();

  const isChileMarket = useMemo(() => {
    return account.company?.marketId === MarketId.Chile;
  }, [account])

  const {
    orderAssortmentQuery: { data: orderAssortment },
    recommendationAssortmentQuery: { data: recommendationAssortment },
  } = useOrdersState();
  const [filters, setFilters] = useState<string[]>([]);

  const confirmedCount = useMemo(() => {
    if (!orderAssortment) return 0;
    const { activeOrders } = orderAssortment;
    if (activeOrders === undefined || !activeOrders.length) return 0;

    return activeOrders.filter((order) => isConfirmedOrder(order, isChileMarket)).length;
  }, [orderAssortment, isChileMarket]);

  const forecastCount = useMemo(() => {
    if (!recommendationAssortment) return 0;
    const { recommendations } = recommendationAssortment;
    if (recommendations === undefined || !recommendations.length) return 0;

    return recommendations.filter((recommendation) => !(recommendation.autoSkipped || recommendation.manuallySkipped)).length;
  }, [recommendationAssortment]);

  const skippedCount = useMemo(() => {
    if (!recommendationAssortment) return 0;
    const { recommendations } = recommendationAssortment;
    if (recommendations === undefined || !recommendations.length) return 0;

    return recommendations.filter((recommendation) => recommendation.autoSkipped || recommendation.manuallySkipped).length;
  }, [recommendationAssortment]);

  const buttons = [
    {
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.confirmed` }),
      value: 'confirmed',
      length: confirmedCount,
      state: true,
      sx: { width: 150 },
    },
    {
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.forecast` }),
      value: 'forecast',
      length: forecastCount,
      state: true,
    },
    {
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.skipped` }),
      value: 'skipped',
      length: skippedCount,
      state: true,
    },
  ];
  return (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <Box display={'flex'} flexDirection={'row'} justifyContent={"space-between"} gap={6}>
        <FilterButtonGroup value={'group'} buttons={buttons} handleClick={setFilters} />
        <Box display={'flex'} flexDirection={'row'} gap={2}>
          {isChileMarket && <CreateOrderDialog />}
          <ViewFeedProgramsDialog />
        </Box>
      </Box>
      <Box display={'flex'} flexDirection={'column'} gap={2}>
        {isChileMarket ? (
          <>
            <ChileOrdersList filters={filters} />
            <ChileRecommendationsList filters={filters} />
          </>
        ) : (
          <>
            <StandardOrdersList filters={filters} />
            <StandardRecommendationsList filters={filters} />
          </>
        )}
      </Box>
    </Box>
  );
};

export default OrdersSection;
